import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, Users, MessageSquare, Clock, 
  Check, X, AlertCircle, ArrowLeft, Phone,
  Mail, RefreshCw, LogOut, TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeTab, setActiveTab] = useState('appointments');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const currentToken = localStorage.getItem('admin_token');
      const [statsRes, appointmentsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${currentToken}` } }),
        axios.get(`${API}/admin/appointments`, { headers: { Authorization: `Bearer ${currentToken}` } }),
        axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${currentToken}` } })
      ]);
      setStats(statsRes.data);
      setAppointments(appointmentsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const verifyToken = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem('admin_token');
      await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setIsAuthenticated(true);
      fetchData();
    } catch (error) {
      localStorage.removeItem('admin_token');
      setToken(null);
      setIsAuthenticated(false);
    }
  }, [fetchData]);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token, verifyToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const { access_token } = response.data;
      localStorage.setItem('admin_token', access_token);
      setToken(access_token);
      setIsAuthenticated(true);
      toast.success('Welcome, Dr. Majmundar!');
      fetchData();
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.patch(
        `${API}/admin/appointments/${appointmentId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Appointment ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
    toast.success('Logged out');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-700">Confirmed</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
      case 'completed':
        return <Badge className="bg-blue-100 text-blue-700">Completed</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d2338] via-[#1E2A4A] to-[#4A6FA5] flex items-center justify-center p-4" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Website
          </Link>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#1E2A4A] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#E8EEF5]" />
              </div>
              <h1 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1E2A4A]">
                Admin Dashboard
              </h1>
              <p className="text-sm text-[#7A8FA0] mt-1">
                Avyan Orthopaedics
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  data-testid="admin-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1E2A4A] hover:bg-[#4A6FA5] text-white py-6"
                data-testid="admin-login-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#1E2A4A] py-6">
        <div className="container-custom flex items-center justify-between">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl font-medium text-white">
              Admin Dashboard
            </h1>
            <p className="text-[#E8EEF5]/60 text-sm">
              Manage appointments & contacts
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={refreshing}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#4A6FA5]/10 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#4A6FA5]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E2A4A]">{stats.appointments.total}</p>
                  <p className="text-sm text-[#7A8FA0]">Total Appointments</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E2A4A]">{stats.appointments.pending}</p>
                  <p className="text-sm text-[#7A8FA0]">Pending</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E2A4A]">{stats.appointments.confirmed}</p>
                  <p className="text-sm text-[#7A8FA0]">Confirmed</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#4A6FA5]/10 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-[#4A6FA5]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E2A4A]">{stats.contacts.new}</p>
                  <p className="text-sm text-[#7A8FA0]">New Inquiries</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'appointments' ? 'default' : 'outline'}
            onClick={() => setActiveTab('appointments')}
            className={activeTab === 'appointments' ? 'bg-[#1E2A4A]' : ''}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Appointments ({appointments.length})
          </Button>
          <Button
            variant={activeTab === 'contacts' ? 'default' : 'outline'}
            onClick={() => setActiveTab('contacts')}
            className={activeTab === 'contacts' ? 'bg-[#1E2A4A]' : ''}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Inquiries ({contacts.length})
          </Button>
        </div>

        {/* Appointments List */}
        {activeTab === 'appointments' && (
          <div className="space-y-4">
            {appointments.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <Calendar className="w-12 h-12 text-[#E8EEF5] mx-auto mb-4" />
                <p className="text-[#7A8FA0]">No appointments yet</p>
              </div>
            ) : (
              appointments.map((apt) => (
                <motion.div
                  key={apt.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 shadow-sm"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-[#4A6FA5]/10 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-lg font-bold text-[#4A6FA5]">
                          {format(new Date(apt.date), 'd')}
                        </span>
                        <span className="text-[10px] uppercase text-[#7A8FA0]">
                          {format(new Date(apt.date), 'MMM')}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusBadge(apt.status)}
                          <span className="text-sm text-[#4A6FA5] font-medium">{apt.time_slot}</span>
                        </div>
                        <p className="font-semibold text-[#1E2A4A]">{apt.user_name}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#7A8FA0]">
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {apt.user_phone}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {apt.user_email}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-[#5e7080]">
                          <strong>Concern:</strong> {apt.concern}
                        </p>
                        {apt.notes && (
                          <p className="text-sm text-[#7A8FA0]">
                            <strong>Notes:</strong> {apt.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {apt.status !== 'cancelled' && apt.status !== 'completed' && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Select
                          value={apt.status}
                          onValueChange={(value) => updateAppointmentStatus(apt.id, value)}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        <a href={`tel:${apt.user_phone}`}>
                          <Button variant="outline" size="icon">
                            <Phone className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* Contacts List */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {contacts.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <MessageSquare className="w-12 h-12 text-[#E8EEF5] mx-auto mb-4" />
                <p className="text-[#7A8FA0]">No contact inquiries yet</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <motion.div
                  key={contact.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 shadow-sm"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={contact.status === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}>
                          {contact.status}
                        </Badge>
                        <span className="text-xs text-[#7A8FA0]">
                          {format(new Date(contact.created_at), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                      <p className="font-semibold text-[#1E2A4A]">{contact.name}</p>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#7A8FA0]">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {contact.phone}
                        </span>
                        {contact.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {contact.email}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-[#5e7080]">
                        <strong>Concern:</strong> {contact.concern}
                      </p>
                      {contact.message && (
                        <p className="mt-1 text-sm text-[#7A8FA0]">{contact.message}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <a href={`tel:${contact.phone}`}>
                        <Button className="bg-[#4A6FA5] hover:bg-[#1E2A4A]">
                          <Phone className="w-4 h-4 mr-2" />
                          Call
                        </Button>
                      </a>
                      <a href={`https://wa.me/${contact.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline">
                          WhatsApp
                        </Button>
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
